import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Seo } from '../components/Seo';
import { RecipeCard } from '../components/RecipeCard';
import { ArticleCard } from '../components/ArticleParts';
import { recipes } from '../content/recipes';
import { knowledgeArticles } from '../content/knowledge';
import { magazineArticles } from '../content/magazine';

export default function Home() {
  return (
    <>
      <Seo
        title="Felix Genuss | Sternenküche im Napf für kleine Hunde"
        description="Felix Genuss: hochwertige Rezepte, Zutatenwissen und Inspiration für kleine vierbeinige Feinschmecker. Entdecke Sternenküche im Napf."
        path="/"
      />

      <section className="border-b border-brand-line" aria-labelledby="hero-heading">
        <div className="container-site grid items-center gap-10 py-12 sm:py-16 lg:grid-cols-2 lg:gap-16">
          <div className="animate-fade-up">
            <p className="eyebrow">Feinschmecker · seit 2025</p>
            <h1 id="hero-heading" className="mt-4 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Felix Genuss – Sternenküche im Napf
            </h1>
            <p className="mt-4 text-base font-semibold text-brand-olive md:text-lg">Gesunde Rezepte für kleine Hunde</p>
            <p className="mt-5 max-w-xl leading-relaxed text-brand-brown/85">
              Willkommen bei Felix Genuss. Hier dreht sich alles um hochwertige Rezepte, sorgfältig ausgewählte Zutaten und die Freude daran, auch für unsere vierbeinigen Feinschmecker etwas Besonderes zuzubereiten.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/rezepte" className="btn-primary" data-testid="hero-cta-rezepte">
                Rezepte entdecken
              </Link>
              <Link to="/felix" className="btn-secondary" data-testid="hero-cta-felix">
                Felix kennenlernen
              </Link>
            </div>
          </div>
          <div className="animate-fade-up [animation-delay:150ms]">
            <img
              src="/assets/brand/sternekueche-hero-1280.webp"
              srcSet="/assets/brand/sternekueche-hero-800.webp 800w, /assets/brand/sternekueche-hero-1280.webp 1280w, /assets/brand/sternekueche-hero-1600.webp 1600w"
              sizes="(min-width: 1024px) 50vw, 100vw"
              width="1280"
              height="714"
              alt="Felix, ein heller Bolonka Zwetna, sitzt neben dem Schriftzug Sterneküche im Napf"
              fetchPriority="high"
              className="h-auto w-full border border-brand-gold/30"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20" aria-labelledby="was-ist-heading">
        <div className="container-site grid items-center gap-10 lg:grid-cols-[auto_1fr] lg:gap-16">
          <img
            src="/assets/brand/logo-512.webp"
            alt="Rundes Felix-Genuss-Logo mit dem Schriftzug Feinschmecker seit 2025"
            width="512"
            height="512"
            loading="lazy"
            className="mx-auto w-40 sm:w-48 lg:mx-0"
          />
          <div>
            <p className="eyebrow">Was ist Felix Genuss?</p>
            <h2 id="was-ist-heading" className="mt-3 font-serif text-3xl font-bold leading-tight sm:text-4xl">
              Genuss beginnt mit guten Zutaten
            </h2>
            <hr className="gold-rule mt-6" />
            <p className="mt-6 max-w-2xl leading-relaxed text-brand-brown/85">
              Felix Genuss steht für die Freude an hochwertigem Hundefutter, guten Zutaten und liebevoll zubereiteten Rezepten. Im Mittelpunkt stehen zunächst kleine Hunde und ihre besonderen Bedürfnisse – verbunden mit einer grossen Portion Genuss. Unsere Rezepte sind verständlich erklärt, nachvollziehbar zubereitet und transparent beschrieben.
            </p>
            <Link to="/felix" className="btn-secondary mt-8" data-testid="cta-mehr-ueber-felix-genuss">
              Mehr über Felix Genuss
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-brand-beige py-14 sm:py-20" aria-labelledby="rezepte-heading">
        <div className="container-site">
          <div className="max-w-2xl">
            <p className="eyebrow">Die Rezeptküche</p>
            <h2 id="rezepte-heading" className="mt-3 font-serif text-3xl font-bold leading-tight sm:text-4xl">
              Rezepte für anspruchsvolle Hunde
            </h2>
            <p className="mt-5 leading-relaxed text-brand-brown/85">
              Für Feinschmecker auf vier Pfoten: eine wachsende Sammlung ausgewählter Rezepte für Hunde, die gutes Essen genauso zu schätzen wissen wie ihre Menschen.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {recipes.slice(0, 3).map((r) => (
              <RecipeCard key={r.slug} recipe={r} />
            ))}
          </div>
          <div className="mt-10">
            <Link to="/rezepte" className="btn-primary" data-testid="cta-alle-rezepte">
              Alle Rezepte entdecken
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20" aria-labelledby="wissen-heading">
        <div className="container-site">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Zutatenwissen</p>
              <h2 id="wissen-heading" className="mt-3 font-serif text-3xl font-bold leading-tight sm:text-4xl">
                Wissen rund um Hundeernährung
              </h2>
              <p className="mt-5 leading-relaxed text-brand-brown/85">
                Was darf mein Hund essen? Welche Lebensmittel sind tabu? Unser Wissensbereich erklärt es verständlich.
              </p>
            </div>
            <Link to="/wissen" className="link-inline inline-flex items-center gap-2" data-testid="cta-wissen-entdecken">
              Zum Wissensbereich <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {knowledgeArticles.slice(0, 3).map((a) => (
              <ArticleCard key={a.slug} article={a} basePath="/wissen" />
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-brand-line py-14 sm:py-20" aria-labelledby="felix-heading">
        <div className="container-site grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow">Der Feinschmecker</p>
            <h2 id="felix-heading" className="mt-3 font-serif text-3xl font-bold leading-tight sm:text-4xl">
              Hallo, ich bin Felix
            </h2>
            <hr className="gold-rule mt-6" />
            <p className="mt-6 max-w-xl leading-relaxed text-brand-brown/85">
              Felix ist ein Bolonka Zwetna und der Namensgeber von Felix Genuss. Er ist der Feinschmecker hinter unserer Idee und probiert jedes Rezept höchstpersönlich – ehrlich, unbestechlich und mit sichtbarer Begeisterung. Feinschmecker eben, seit 2025.
            </p>
            <Link to="/felix" className="btn-secondary mt-8" data-testid="cta-felix-kennenlernen">
              Felix kennenlernen
            </Link>
          </div>
          <img
            src="/assets/brand/felix-testet-1280.webp"
            srcSet="/assets/brand/felix-testet-800.webp 800w, /assets/brand/felix-testet-1280.webp 1280w, /assets/brand/felix-testet-1600.webp 1600w"
            sizes="(min-width: 1024px) 50vw, 100vw"
            width="1280"
            height="714"
            alt="Felix, ein heller Bolonka Zwetna mit langen Ohren, schaut freundlich in die Kamera"
            loading="lazy"
            className="h-auto w-full border border-brand-gold/30"
          />
        </div>
      </section>

      <section className="py-14 sm:py-20" aria-labelledby="magazin-heading">
        <div className="container-site">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Das Magazin</p>
              <h2 id="magazin-heading" className="mt-3 font-serif text-3xl font-bold leading-tight sm:text-4xl">
                Neu im Magazin
              </h2>
            </div>
            <Link to="/magazin" className="link-inline inline-flex items-center gap-2" data-testid="cta-alle-beitraege">
              Alle Beiträge <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {magazineArticles.slice(0, 3).map((a) => (
              <ArticleCard key={a.slug} article={a} basePath="/magazin" />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
